import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import {
  getDefaultOptions,
  getProductTrackingData,
  ReactPixel,
  track,
  transformImagesToPhotoswipe,
  updatePhotoswipeImages,
} from '../../helpers';
import { Carousel, PhotoSwipe } from '../../components_common/customized_modules';
import { Wrapper, WrapperIfNotEmpty } from '../css_global/utils.css';
import { updateOverflowScroll } from '../../modules/manageOverflowScroll';
import InStockOptions from '../components/InStockOptions';
import RelatedProducts from '../components/RelatedProducts';
import ShareButtons from '../components/ShareButtons';
import SubmitButton from '../components/SubmitButton';
import { addToCart } from '../../common/actions/cartActions';
import ProductDetailsProductInfo from '../../components_common/ProductInfoCard/ProductDetailsProductInfo';
import productInfoDataSanitizer from '../../components_common/ProductInfoCard/productInfoDataSanitizer';
import ProductNotFoundCard from '../../components_common/ProductNotFoundCard/ProductNotFoundCard';
import generateProductDetailsDescriptionData from '../../helpers/productDetailsDescriptionHelper';
import ProductDetailsDescriptionGroup from '../../components_common/ProductDetailsDescriptionGroup/ProductDetailsDescriptionGroup';
import TrackingEvents from '../../common/models/TrackingEvents';
import ProductMicrodata from '../../components_common/Microdata/ProductMicrodata';
import withCountryCode from '../../components/RoutesHoc/RoutesHoc';
import { normalizeSizeSelectorOptionsOrder, normalizeSizeValueName } from '../../helpers/sizeHelper';
import { formatModelNumber } from '../../helpers/pdpHelpers';
import EyewearWarning from '../../components/EyewearWarning';
import { shouldDisplayEyewearWarning } from '../../common/models/Category';
import ProductExcludedModal from '../../components/ProductExcludedModal/ProductExcludedModal';
import timerQueue, { EventType } from '../../helpers/TimerQueue';

/* eslint-disable @typescript-eslint/camelcase */
const isProductUnavailable = product => isEmpty(product) || product.product_is_active === 'N';

const mapStateToProps = ({
  api: {
    category_id,
    countryId,
    currencyIso,
    currencySymbol,
    fbOg,
    filterSettings,
    freeShipping,
    path,
    product,
    product: { size_unit_system },
    s3BasePath,
    trackingProductData,
    trackingSizeData,
    twitter,
  },
  countryCode,
  department,
}) => ({
  categoryId: category_id,
  countryCode,
  countryId,
  currencyIso,
  currencySymbol,
  department,
  fbOg,
  filterSettings,
  freeShipping,
  path,
  product,
  s3BasePath,
  trackingProductData,
  trackingSizeData,
  twitter,
});

const mapDispatchToProps = dispatch => ({
  updateOverflowScroll: value => {
    dispatch(updateOverflowScroll(value));
  },
  handleAddProduct: productVersionOptionId => dispatch(addToCart(productVersionOptionId)),
});

class ProductDetail extends React.Component {
  constructor(props) {
    const { product, trackingProductData, trackingSizeData } = props;
    super(props);
    this.handleCarouselSlideChange = this.handleCarouselSlideChange.bind(this);
    this.handlePhotoSwipeClose = this.handlePhotoSwipeClose.bind(this);
    this.handlePhotoSwipeOpen = this.handlePhotoSwipeOpen.bind(this);
    this.handleSizeClick = this.handleSizeClick.bind(this);
    this.redirectToCart = this.redirectToCart.bind(this);

    if (isProductUnavailable(product)) {
      return;
    }

    this.state = {
      addToCartText: 'Add to Cart',
      currentImageIndex: 0,
      isPhotoSwipeOpen: false,
      photoSwipeOptions: {
        /* defined in handlePhotoSwipeOpen invocation */
      },
      trackingProductData,
      selectedSize: '',
      trackingSizeData,
    };
  }

  componentDidMount() {
    const { product, s3BasePath, currencySymbol } = this.props;
    const { images, options, isExcluded } = product;

    if (isExcluded) {
      this.trackingPdpBlockers(TrackingEvents.productUnavailable());
      return;
    }

    if (isProductUnavailable(product)) {
      this.trackingPdpBlockers(TrackingEvents.productUnavailable());
      return;
    }

    if (!images || images.length === 0) {
      this.trackingPdpBlockers(TrackingEvents.productNoImages());
      return;
    }

    track(TrackingEvents.productsDetailViewed(), getProductTrackingData(product, currencySymbol, { pdpPage: true }));

    const selectorOptions = options.map(({ nr_available }) => ({
      disabled: nr_available < 1,
    }));
    const isOutOfStock = selectorOptions.every(option => option.disabled);

    if (isOutOfStock) {
      this.trackingPdpBlockers(TrackingEvents.productOutOfStock());
    }

    const carouselImages = images.map(image => (
      <img
        key={image.image_id}
        onClick={this.handlePhotoSwipeOpen}
        onError={e => (e.target.src = `${s3BasePath}mkt/icon/no_image.png`)}
        src={image.medium}
        data-cy="carousel-image-product"
      />
    ));

    const photoSwipeImages = transformImagesToPhotoswipe(images);

    this.setState({ carouselImages, photoSwipeImages });

    const availableOptions = options.reduce((available, option) => {
      if (option.nr_available > 0) {
        available.push(option);
      }
      return available;
    }, []);

    // if only one size available, automatically select it
    if (availableOptions.length === 1) {
      this.setState({ selectedSize: availableOptions[0].product_version_option_id });
    }
  }

  trackingPdpBlockers(eventName) {
    const { department, product } = this.props;
    const {
      category,
      product_id,
      brand,
      model,
      color,
      isExcluded,
      model_number,
      season,
      store,
      product_is_active,
    } = product;

    track(eventName, {
      categoryDepartment: {
        department: department.toLowerCase(),
      },
      product: {
        product_id,
        brand,
        model,
        category,
        color,
        is_excluded: isExcluded,
        model_number,
        season,
        store,
        product_is_active,
      },
    });
  }

  handlePhotoSwipeOpen(e) {
    e.preventDefault();
    this.props.updateOverflowScroll('initial');
    const images = updatePhotoswipeImages(this.state.photoSwipeImages);
    this.setState({
      isPhotoSwipeOpen: true,
      photoSwipeImages: images,
      photoSwipeOptions: getDefaultOptions(this.state.currentImageIndex),
    });
  }

  handlePhotoSwipeClose(instance) {
    this.props.updateOverflowScroll('touch');
    this.setState({
      currentImageIndex: instance.getCurrentIndex(),
      isPhotoSwipeOpen: false,
    });
  }

  handleCarouselSlideChange(currentIndex) {
    const { currentImageIndex } = this.state;
    this.setState({ currentImageIndex: currentIndex });
    if (currentImageIndex !== currentIndex) {
      track(TrackingEvents.productImageViewed());
    }
  }

  handleSizeClick(e) {
    if (this.state.selectedSize !== e.target.value) {
      this.setState({
        addToCartText: 'Add to Cart',
        selectedSize: e.target.value,
      });
    }
  }

  redirectToCart(e, isOutOfStock) {
    e.preventDefault();
    const {
      product: { size_unit_system, navigation_path },
      handleAddProduct,
    } = this.props;
    const { selectedSize } = this.state;

    const catUrl = navigation_path[navigation_path.length - 1].url;
    if (!selectedSize) {
      if (isOutOfStock) {
        window.location = `/${catUrl}`;
      }
      // The value of addToCartText must change with subsequent clicks in order
      // for the pulse prop on OptionSelector repeatedly to cause a re-render
      this.setState({ addToCartText: '' }, () => this.setState({ addToCartText: 'Select a Size' }));
    } else {
      const { product, currencySymbol } = this.props;
      timerQueue.addEvent({
        callback: () =>
          track(
            TrackingEvents.productAdded(),
            getProductTrackingData(product, currencySymbol, { productVersionOptionId: selectedSize }),
          ),
        type: EventType.UserEvent,
      });

      handleAddProduct(selectedSize);
    }
  }

  renderProductUnavailable() {
    const {
      countryCode,
      currencyIso,
      freeShipping,
      product: { category, category_path, model, images, rrp },
      buildUrlWithCountry,
    } = this.props;

    let url = null;
    if (category_path) {
      url = buildUrlWithCountry(`/${category_path.ancestors}/${category_path.category_id}`);
    }

    return (
      <ProductNotFoundCard
        buttonText={category}
        buttonUrl={url}
        model={model}
        isOutOfStock
        image={images && images.length > 0 ? images[0].medium : null}
        countryCode={countryCode}
        currencyIso={currencyIso}
        freeShipping={freeShipping}
        isDesktop
        price={rrp}
      />
    );
  }

  renderProductDetails() {
    const {
      categoryId,
      countryId,
      countryCode,
      currencySymbol,
      currencyIso,
      department,
      fbOg,
      freeShipping,
      path,
      product,
      product: {
        brand,
        brand_url,
        category,
        category_path,
        isExcluded,
        color,
        description,
        images,
        model,
        model_number,
        navigation_path,
        options,
        related_products,
        rrp,
        size_fit,
        size_unit_system: sizeUnitSystem,
      },
      s3BasePath,
      twitter,
      buildUrlWithCountry,
    } = this.props;
    const {
      addToCartText,
      carouselImages,
      currentImageIndex,
      isPhotoSwipeOpen,
      photoSwipeImages,
      photoSwipeOptions,
      selectedSize,
    } = this.state;

    const selectorTitleText = sizeUnitSystem ? `Size: ${sizeUnitSystem}` : 'Size';
    const selectorOptions = options.map(({ nr_available, product_version_option_id: value, size: rawSize }) => {
      const size = normalizeSizeValueName(rawSize);
      const disabled = nr_available < 1;
      let label;
      if (selectedSize === value) {
        label = `Selected Size: ${size}`;
      } else {
        label = `${size}${disabled ? ' (Out of Stock)' : ''}`;
      }
      return { disabled, label, value, rawSize };
    });
    const isOutOfStock = selectorOptions.every(option => option.disabled);
    const outOfStockButtonText = `Back to ${navigation_path[navigation_path.length - 1].name}`;

    const selectorOptionsOrdered = normalizeSizeSelectorOptionsOrder(selectorOptions, sizeUnitSystem);

    return (
      <>
        <div
          data-cy="wrapper-product-detail"
          data-cy-pid={product.product_id}
          data-cy-pvid={product.product_version_id}
          data-cy-pvoid={selectedSize}
        >
          <PhotoSwipe
            close={this.handlePhotoSwipeClose}
            isOpen={isPhotoSwipeOpen}
            items={photoSwipeImages}
            options={photoSwipeOptions}
          />
          {isExcluded && <ProductExcludedModal category={category} categoryPath={category_path} />}
          <Wrapper borderBottom="3px solid #cccccc" borderTop="3px solid #cccccc" data-cy="product-detail-image">
            <Carousel
              afterSlide={this.handleCarouselSlideChange}
              displayButtons={['SlideIndicators', 'ZoomButton']}
              dragging
              easing="easeInOutSine"
              edgeEasing="easeInOutSine"
              imageWidth="70%"
              imageMaxWidth="520px"
              openPhotoSwipe={this.handlePhotoSwipeOpen}
              s3BasePath={s3BasePath}
              slideIndex={currentImageIndex}
              speed={200}
              swiping
              wrapAround
            >
              {carouselImages}
            </Carousel>
          </Wrapper>

          <Wrapper padding="5%" data-cy="product-detail-info-card">
            <ProductDetailsProductInfo
              currencySymbol={currencySymbol}
              product={productInfoDataSanitizer(product)}
              showSale={freeShipping}
            />
          </Wrapper>
          <Wrapper padding="0 5%" fontSize="15px">
            Color: {color}
          </Wrapper>

          <Wrapper margin="5%">
            <InStockOptions
              categories={navigation_path[navigation_path.length - 1].url}
              currentCategoryId={categoryId}
              color={isOutOfStock ? '#C30016' : null}
              data-cy="product-detail-size-selector"
              department={department}
              handleSizeClick={this.handleSizeClick}
              onSelectorClick={() => {
                track(TrackingEvents.sizeListViewed(), {
                  ui: {
                    size_system: sizeUnitSystem,
                    sizes: selectorOptions.map(el => el.label),
                  },
                });
              }}
              isOutOfStock={isOutOfStock}
              s3BasePath={s3BasePath}
              selectedOption={selectedSize}
              selectorOptions={selectorOptionsOrdered}
              selectorTitleText={isOutOfStock ? 'sold out' : selectorTitleText}
              shouldPulse={addToCartText === selectorTitleText}
            />
          </Wrapper>

          <SubmitButton
            buttonText={isOutOfStock ? outOfStockButtonText : addToCartText}
            handleClick={e => this.redirectToCart(e, isOutOfStock)}
            margin="5%"
            validated={selectedSize !== selectorTitleText}
            dataCy="button-add-to-cart"
          />

          {model_number && (
            <Wrapper padding="0 5%">
              <span>Model number: {formatModelNumber(model_number)}</span>
            </Wrapper>
          )}

          <WrapperIfNotEmpty padding="0 5%">
            {shouldDisplayEyewearWarning(categoryId, countryId) ? <EyewearWarning /> : null}
          </WrapperIfNotEmpty>

          <ShareButtons s3BasePath={s3BasePath} img={images ? images[0].medium : null} fbOg={fbOg} twitter={twitter} />

          <ProductDetailsDescriptionGroup
            s3BasePath={s3BasePath}
            data={generateProductDetailsDescriptionData(description, size_fit, currencySymbol, buildUrlWithCountry)}
          />

          <RelatedProducts
            brandName={brand}
            brandUrl={brand_url}
            currencySymbol={currencySymbol}
            department={department}
            path={path}
            products={related_products}
            s3BasePath={s3BasePath}
            showSale={freeShipping}
          />
        </div>
        <ProductMicrodata
          countryCode={countryCode}
          currencyIso={currencyIso}
          freeShipping={freeShipping}
          image={images ? images[0].medium : null}
          isOutOfStock={isOutOfStock || isExcluded}
          price={rrp}
          productName={model}
        />
      </>
    );
  }

  render() {
    const { product } = this.props;
    return isProductUnavailable(product) || !product.images || product.images.length === 0
      ? this.renderProductUnavailable()
      : this.renderProductDetails();
  }
}

ProductDetail.propTypes = {
  categoryId: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  filterSettings: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  product: PropTypes.object.isRequired,
  s3BasePath: PropTypes.string.isRequired,
  updateOverflowScroll: PropTypes.func.isRequired,
  trackingProductData: PropTypes.object,
  trackingSizeData: PropTypes.object,
  handleAddProduct: PropTypes.func.isRequired,
  buildUrlWithCountry: PropTypes.func.isRequired,
};

ProductDetail = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProductDetail);
export default withCountryCode(ProductDetail);
